/**
 * Checkout Error Recovery Utilities
 *
 * Handles detection and recovery from transient checkout errors like expired offers.
 * Uses structured error responses per V2 Checkout API contract.
 */

import type {
  CheckoutErrorDetail,
  CheckoutErrorResponse,
  RecoveryAction,
} from '../../transport/types/checkout';

// Re-export for backward compatibility
export type {CheckoutErrorDetail, CheckoutErrorResponse, RecoveryAction};

interface AxiosLikeError {
  response?: {
    status?: number;
    data?: CheckoutErrorResponse | string;
  };
}

export interface ParsedCheckoutError {
  isRecoverable: boolean;
  recoveryAction: RecoveryAction | null;
  code: string | null;
  message: string;
  details?: CheckoutErrorDetail[];
}

/**
 * Parses a checkout error to extract recovery information.
 *
 * @param err - The error from checkout API call
 * @returns Parsed recovery info or null if not recoverable
 */
export function parseCheckoutError(err: unknown): ParsedCheckoutError | null {
  if (typeof err !== 'object' || err === null || !('response' in err)) {
    return null;
  }

  const axiosErr = err as AxiosLikeError;
  if (axiosErr.response?.status !== 422) {
    return null;
  }

  const data = axiosErr.response?.data;

  // Handle legacy string responses
  if (typeof data === 'string') {
    return {
      isRecoverable: false,
      recoveryAction: null,
      code: null,
      message: data,
    };
  }

  // Handle new structured response
  if (data && typeof data === 'object' && 'recoverable' in data) {
    return {
      isRecoverable: data.recoverable,
      recoveryAction: data.recovery_action || null,
      code: data.code || null,
      message: data.message || 'Checkout failed',
      details: data.details,
    };
  }

  return null;
}
